
import React from 'react';
import * as LucideIcons from 'lucide-react';

// Mapeamento de ícones sociais comuns
const socialIcons: Record<string, React.ComponentType<any>> = {
  instagram: LucideIcons.Instagram,
  facebook: LucideIcons.Facebook,
  twitter: LucideIcons.Twitter,
  linkedin: LucideIcons.Linkedin,
  youtube: LucideIcons.Youtube,
  tiktok: LucideIcons.Music, // TikTok não tem ícone específico
  whatsapp: LucideIcons.MessageCircle,
  telegram: LucideIcons.Send,
  email: LucideIcons.Mail,
  phone: LucideIcons.Phone,
  website: LucideIcons.Globe,
  link: LucideIcons.Link,
  home: LucideIcons.Home,
  user: LucideIcons.User,
  heart: LucideIcons.Heart,
  star: LucideIcons.Star,
  music: LucideIcons.Music,
  video: LucideIcons.Video,
  image: LucideIcons.Image,
  file: LucideIcons.File,
  download: LucideIcons.Download,
  shop: LucideIcons.ShoppingBag,
  cart: LucideIcons.ShoppingCart,
  gift: LucideIcons.Gift,
  calendar: LucideIcons.Calendar,
  clock: LucideIcons.Clock,
  map: LucideIcons.Map,
  camera: LucideIcons.Camera,
  mic: LucideIcons.Mic,
  headphones: LucideIcons.Headphones,
  gamepad: LucideIcons.Gamepad2,
  book: LucideIcons.Book,
  bookmark: LucideIcons.Bookmark,
  coffee: LucideIcons.Coffee,
  pizza: LucideIcons.Pizza,
  car: LucideIcons.Car,
  plane: LucideIcons.Plane,
  building: LucideIcons.Building,
  briefcase: LucideIcons.Briefcase,
  graduation: LucideIcons.GraduationCap,
  trophy: LucideIcons.Trophy,
  target: LucideIcons.Target,
  zap: LucideIcons.Zap,
  flame: LucideIcons.Flame,
  sun: LucideIcons.Sun,
  moon: LucideIcons.Moon,
  cloud: LucideIcons.Cloud,
  umbrella: LucideIcons.Umbrella,
  tree: LucideIcons.Trees,
  flower: LucideIcons.Flower,
  gem: LucideIcons.Gem,
  crown: LucideIcons.Crown,
  shield: LucideIcons.Shield,
  key: LucideIcons.Key,
  lock: LucideIcons.Lock,
  unlock: LucideIcons.Unlock,
  eye: LucideIcons.Eye,
  search: LucideIcons.Search,
  plus: LucideIcons.Plus,
  minus: LucideIcons.Minus,
  check: LucideIcons.Check,
  x: LucideIcons.X,
  arrow: LucideIcons.ArrowRight,
  external: LucideIcons.ExternalLink
};

// Função para detectar emojis
const isEmoji = (str: string): boolean => {
  const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u;
  return emojiRegex.test(str);
};

// Componente para renderizar emoji
const EmojiIcon: React.FC<{ emoji: string; className?: string }> = ({ emoji, className = '' }) => (
  <span className={`inline-block ${className}`} style={{ fontSize: '1.2em' }}>
    {emoji}
  </span>
);

// Função principal para obter o componente do ícone
export const getIconComponent = (iconName?: string): React.ComponentType<any> | null => {
  if (!iconName) return null;

  // Verificar se é emoji
  if (isEmoji(iconName)) {
    return (props: any) => <EmojiIcon emoji={iconName} {...props} />;
  }

  // Buscar nos ícones sociais/comuns primeiro
  const normalizedName = iconName.toLowerCase().replace(/[^a-zA-Z0-9]/g, '');
  if (socialIcons[normalizedName]) {
    return socialIcons[normalizedName];
  }

  // Buscar nos ícones do Lucide (capitalizado)
  const lucideName = iconName.charAt(0).toUpperCase() + iconName.slice(1).toLowerCase();
  if ((LucideIcons as any)[lucideName]) {
    return (LucideIcons as any)[lucideName];
  }

  // Fallback para ícone de link
  return LucideIcons.Link;
};

// Lista de ícones disponíveis para seleção
export const availableIcons = [
  // Redes sociais
  { name: 'Instagram', value: 'instagram' },
  { name: 'Facebook', value: 'facebook' },
  { name: 'Twitter', value: 'twitter' },
  { name: 'LinkedIn', value: 'linkedin' },
  { name: 'YouTube', value: 'youtube' },
  { name: 'TikTok', value: 'tiktok' },
  { name: 'WhatsApp', value: 'whatsapp' },
  { name: 'Telegram', value: 'telegram' },
  
  // Contato
  { name: 'Email', value: 'email' },
  { name: 'Telefone', value: 'phone' },
  { name: 'Website', value: 'website' },
  { name: 'Link', value: 'link' },
  
  // Geral
  { name: 'Home', value: 'home' },
  { name: 'Usuário', value: 'user' },
  { name: 'Coração', value: 'heart' },
  { name: 'Estrela', value: 'star' },
  { name: 'Música', value: 'music' },
  { name: 'Vídeo', value: 'video' },
  { name: 'Imagem', value: 'image' },
  { name: 'Arquivo', value: 'file' },
  { name: 'Download', value: 'download' },
  { name: 'Loja', value: 'shop' },
  { name: 'Carrinho', value: 'cart' },
  { name: 'Presente', value: 'gift' },
  { name: 'Calendário', value: 'calendar' },
  { name: 'Relógio', value: 'clock' },
  { name: 'Mapa', value: 'map' },
  { name: 'Câmera', value: 'camera' },
  { name: 'Microfone', value: 'mic' },
  { name: 'Fones', value: 'headphones' },
  { name: 'Gamepad', value: 'gamepad' },
  { name: 'Livro', value: 'book' },
  { name: 'Favorito', value: 'bookmark' },
  { name: 'Café', value: 'coffee' },
  { name: 'Pizza', value: 'pizza' },
  { name: 'Carro', value: 'car' },
  { name: 'Avião', value: 'plane' },
  { name: 'Prédio', value: 'building' },
  { name: 'Maleta', value: 'briefcase' },
  { name: 'Graduação', value: 'graduation' },
  { name: 'Troféu', value: 'trophy' },
  { name: 'Alvo', value: 'target' },
  { name: 'Raio', value: 'zap' },
  { name: 'Fogo', value: 'flame' }
];

// Emojis populares
export const popularEmojis = [
  '🚀', '💎', '🎯', '🔥', '⭐', '💫', '🌟', '✨',
  '🎉', '🎊', '🎈', '🎁', '🏆', '🥇', '🎖️', '🏅',
  '💪', '👑', '🦄', '🌈', '🎨', '🎭', '🎪', '🎬',
  '📱', '💻', '🖥️', '📺', '📷', '📹', '🎥', '📞',
  '💌', '📧', '📩', '📨', '📬', '📪', '📫', '📮',
  '🛍️', '🛒', '💳', '💰', '💸', '💵', '💴', '💶',
  '🏠', '🏢', '🏪', '🏬', '🏭', '🏯', '🏰', '⛪',
  '🎓', '📚', '📖', '📝', '✏️', '🖊️', '🖋️', '✒️',
  '🍕', '🍔', '🍟', '🌭', '🥪', '🌮', '🌯', '🥙',
  '☕', '🍺', '🍻', '🥂', '🍷', '🥃', '🍸', '🍹',
  '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑',
  '✈️', '🚁', '🚂', '🚆', '🚇', '🚈', '🚉', '🚊',
  '🌞', '🌝', '🌛', '🌜', '🌚', '🌕', '🌖', '🌗',
  '🌸', '🌺', '🌻', '🌷', '🌹', '🥀', '🌾', '🌿'
];
